/**
 * useVoice — Reusable voice hook for all chat/Einstein interfaces.
 * - speak(text)       → Kokoro TTS (am_adam deep male voice via backend)
 * - startListening()  → Browser STT (SpeechRecognition API)
 * - voiceEnabled      → toggleable by user
 * - isListening       → true while mic is active
 * - isSpeaking        → true while audio is playing
 */
import { useState, useRef, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export function useVoice({ autoSpeak = false, voice = 'am_adam' } = {}) {
  const [voiceEnabled, setVoiceEnabled] = useState(autoSpeak);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);

  const speak = useCallback(async (text) => {
    if (!voiceEnabled || !text?.trim()) return;
    // Stop any current audio
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    try {
      setIsSpeaking(true);
      const { data } = await axios.post(`${API}/api/voice/tts`, {
        text: text.replace(/[*#`]/g, '').slice(0, 500),
        voice,
        speed: 0.95,
      });
      if (!data.audio_base64) { setIsSpeaking(false); return; }
      const bytes = atob(data.audio_base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([arr], { type: 'audio/wav' }));
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setIsSpeaking(false); URL.revokeObjectURL(url); audioRef.current = null; };
      audio.onerror = () => { setIsSpeaking(false); audioRef.current = null; };
      await audio.play();
    } catch {
      setIsSpeaking(false);
    }
  }, [voiceEnabled]);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setIsSpeaking(false);
  }, []);

  const startListening = useCallback((onTranscript) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Voice recognition not supported in this browser.'); return; }
    if (isListening) { recognitionRef.current?.stop(); setIsListening(false); return; }
    const r = new SR();
    r.continuous = false;
    r.interimResults = false;
    r.lang = 'en-US';
    r.onresult = (e) => { onTranscript(e.results[0][0].transcript); setIsListening(false); };
    r.onend = () => setIsListening(false);
    r.onerror = () => setIsListening(false);
    recognitionRef.current = r;
    r.start();
    setIsListening(true);
  }, [isListening]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  return {
    voiceEnabled, setVoiceEnabled,
    isListening, isSpeaking,
    speak, stopSpeaking,
    startListening, stopListening,
  };
}
